import { useState, useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppShell } from './components/AppShell.js';
import { LoadingState } from './components/ui/LoadingState.js';
import { EmptyState } from './components/ui/EmptyState.js';
import { Button } from './components/ui/Button.js';
import { SearchOverlay } from './components/SearchOverlay.js';
import { LoginPage } from './pages/LoginPage.js';
import { SetPasswordPage } from './pages/SetPasswordPage.js';
import { ContactsPage } from './pages/ContactsPage.js';
import { ContactDetailPage } from './pages/ContactDetailPage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { EmailPage } from './pages/EmailPage.js';
import { LeadsPage } from './pages/LeadsPage.js';
import { FunnelsListPage } from './pages/FunnelsListPage.js';
import { FunnelsPage } from './pages/FunnelsPage.js';
import { FunnelEditorPage } from './pages/FunnelEditorPage.js';
import { LeadDetailPage } from './pages/LeadDetailPage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { ShowcasePage } from './pages/ShowcasePage.js';
import { ReportsPage } from './pages/ReportsPage.js';
import { BillingPage } from './pages/BillingPage.js';
import { TasksPage } from './pages/TasksPage.js';
import { MessagingTriagePage } from './pages/MessagingTriagePage.js';
import { MessagesPage } from './pages/MessagesPage.js';
import { FormsPage } from './pages/FormsPage.js';
import { FormDetailPage } from './pages/FormDetailPage.js';
import { ProjectsPage } from './pages/ProjectsPage.js';
import { ProjectDetailPage } from './pages/ProjectDetailPage.js';
import { OnboardingPage } from './pages/OnboardingPage.js';
import { PlatformAdminPage } from './pages/PlatformAdminPage.js';
import { PlatformAccountsPage } from './pages/PlatformAccountsPage.js';
import { PlatformAccountDetailPage } from './pages/PlatformAccountDetailPage.js';
import { PlatformPlansPage } from './pages/PlatformPlansPage.js';
import { WelcomePage } from './pages/WelcomePage.js';
import { ActivarPage } from './pages/ActivarPage.js';
import { AssistantHistoryPage } from './pages/AssistantHistoryPage.js';
import { AssistantMemoryPage } from './pages/AssistantMemoryPage.js';
import { api, ApiError } from './lib/api.js';
import { useAllSettings } from './hooks/useSettings.js';
import { useOnboardingProgress, isOnboardingComplete } from './hooks/useOnboarding.js';
import { SessionProvider, type SessionInfo } from './contexts/SessionContext.js';
import { allModulesEnabled, type AccountModules } from '@evo-hub/shared';
import { installAudioUnlock } from './lib/audioUnlock.js';

const AUTH_ME_MAX_ATTEMPTS = 4;
const AUTH_ME_RETRY_DELAYS_MS = [1000, 2000, 4000];

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export default function App() {
  const { t, i18n } = useTranslation();
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  // Bug real (2026-08-05): antes, CUALQUIER fallo de /api/auth/me (401 real,
  // 500 transitorio, un hiccup de red) mandaba a la pantalla de login — un
  // 500 puntual no significa que la sesión sea inválida, pero el usuario lo
  // vivía exactamente igual: "se cerró la sesión sola". Ahora: 401 real →
  // logout inmediato (la sesión de verdad no es válida). Cualquier otro
  // error → reintenta con backoff; si se agotan los intentos, se muestra un
  // estado de error explícito con botón de reintentar — nunca la pantalla
  // de login, porque no sabemos que la sesión sea inválida.
  const [authCheckError, setAuthCheckError] = useState<string | null>(null);
  const [session, setSession] = useState<SessionInfo>({ displayName: null, accountName: null, role: null, isPlatformAdmin: false, modules: allModulesEnabled() });
  const [showSearch, setShowSearch] = useState(false);
  const { data: settings } = useAllSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: onboardingData } = useOnboardingProgress(authenticated === true);
  // Redirige a /onboarding UNA sola vez por sesión de login — si el cliente
  // navega a otra ruta después, no lo interrumpe de nuevo (skippable, per
  // spec). No dispara si aterriza directo en otra ruta (ej. abrió un link
  // directo a /email) — solo cuando entra por el dashboard default.
  const onboardingRedirectCheckedRef = useRef(false);

  // Sync language from DB settings on startup (DB beats localStorage for cross-session persistence)
  useEffect(() => {
    const lang = settings?.['ui_language'];
    if (lang && lang !== i18n.language) {
      void i18n.changeLanguage(lang);
    }
  }, [settings, i18n]);

  async function fetchSession(): Promise<void> {
    setAuthCheckError(null);
    for (let attempt = 1; attempt <= AUTH_ME_MAX_ATTEMPTS; attempt++) {
      try {
        const d = await api.get<{ authenticated: boolean; displayName?: string | null; accountName?: string | null; role?: 'owner' | 'member' | null; isPlatformAdmin?: boolean; modules?: AccountModules }>('/api/auth/me');
        setAuthenticated(d.authenticated);
        if (d.authenticated) {
          setSession({ displayName: d.displayName ?? null, accountName: d.accountName ?? null, role: d.role ?? null, isPlatformAdmin: d.isPlatformAdmin ?? false, modules: d.modules ?? allModulesEnabled() });
        }
        return;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          setAuthenticated(false);
          return;
        }
        const isLastAttempt = attempt === AUTH_ME_MAX_ATTEMPTS;
        if (isLastAttempt) {
          setAuthCheckError(err instanceof Error ? err.message : 'No se pudo confirmar la sesión.');
          return;
        }
        await sleep(AUTH_ME_RETRY_DELAYS_MS[attempt - 1] ?? 4000);
      }
    }
  }

  useEffect(() => {
    void fetchSession();
  }, []);

  // Autoplay: el navegador concede permiso de audio al DOCUMENTO tras el primer
  // gesto. Se engancha desde el arranque para que ese primer clic —el que sea,
  // incluso el del login— ya deje la voz habilitada, en vez de que el permiso
  // recién se pida cuando Evo Brain intenta hablar y para entonces ya sea tarde.
  useEffect(() => { installAudioUnlock(); }, []);

  // La sesión (rol, módulos habilitados) se leía UNA sola vez al montar, así
  // que apagar un módulo desde el panel de plataforma no le pegaba al cliente
  // hasta que recargara. Encontrado probando con villa-sion: los módulos
  // estaban bien apagados en la DB y el backend ya daba 403, pero el menú
  // seguía mostrándolos en la pestaña que ya tenía abierta.
  //
  // Al recuperar el foco se relee. Es el mismo momento en que el usuario vuelve
  // a mirar la pantalla, así que el menú nunca se le actualiza "en la cara".
  useEffect(() => {
    if (!authenticated) return;
    const onFocus = () => { void fetchSession(); };
    const onVisible = () => { if (document.visibilityState === 'visible') onFocus(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [authenticated]);

  useEffect(() => {
    if (!authenticated) { onboardingRedirectCheckedRef.current = false; return; }
    if (onboardingRedirectCheckedRef.current) return;
    if (!onboardingData) return; // esperar a que cargue antes de decidir
    onboardingRedirectCheckedRef.current = true;
    // Solo desde el inicio: si el cliente acaba de volver de Stripe está en
    // /bienvenida, y mandarlo a la inducción justo ahí sería decirle "te falta
    // algo" en el momento en que terminó de pagar.
    // La cuenta operadora no se manda a la inducción (2026-08-31): nunca pasa
    // por Stripe, así que 'activation' no se cierra nunca y cada login
    // aterrizaba ahí. Para los clientes el forzado sigue igual — es el gate que
    // le da sentido a la pantalla. Ver isOnboardingRedirectExempt en el server.
    // La inducción sigue accesible a mano desde /onboarding.
    if (onboardingData.redirectExempt === true) return;
    if (location.pathname === '/' && !isOnboardingComplete(onboardingData.progress)) {
      navigate('/onboarding', { replace: true });
    }
  }, [authenticated, onboardingData, location.pathname, navigate]);

  useEffect(() => {
    if (!authenticated) return;
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(s => !s);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [authenticated]);

  if (authCheckError) {
    return (
      <div
        className="flex h-full items-center justify-center"
        style={{ background: 'var(--evo-bg)' }}
      >
        <EmptyState
          title={t('app.authCheckFailed.title', 'No se pudo confirmar tu sesión')}
          description={t('app.authCheckFailed.description', 'Puede ser un problema de conexión — tu sesión probablemente sigue activa.')}
          action={<Button variant="solid" tone="cyan" onClick={() => { void fetchSession(); }}>{t('app.authCheckFailed.retryBtn', 'Reintentar')}</Button>}
        />
      </div>
    );
  }

  if (authenticated === null) {
    return (
      <div
        className="flex h-full items-center justify-center"
        style={{ background: 'var(--evo-bg)' }}
      >
        <LoadingState fullHeight />
      </div>
    );
  }

  if (!authenticated) {
    return (
      <Routes>
        <Route path="/set-password" element={<SetPasswordPage onSuccess={() => { void fetchSession(); }} />} />
        <Route path="*" element={<LoginPage onSuccess={() => { void fetchSession(); }} />} />
      </Routes>
    );
  }

  return (
    <SessionProvider value={session}>
      <AppShell>
        {showSearch && <SearchOverlay onClose={() => setShowSearch(false)} />}
        {/* Un deep link a un módulo apagado cae a Home. Es cosmética: la
            protección real son los 403 de requireModule en el backend — esto
            evita pintar una pantalla vacía que va a dar error en cada fetch. */}
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route
            path="/admin/signup-requests"
            element={session.isPlatformAdmin ? <PlatformAdminPage /> : <Navigate to="/" replace />}
          />
          {/* Panel de plataforma (2026-08-17). Esta guarda es cosmética: la real
              es requirePlatformAdmin en /api/platform/*, que devuelve 403 aunque
              alguien monte el componente a mano. */}
          <Route
            path="/admin/cuentas"
            element={session.isPlatformAdmin ? <PlatformAccountsPage /> : <Navigate to="/" replace />}
          />
          <Route
            path="/admin/cuentas/:id"
            element={session.isPlatformAdmin ? <PlatformAccountDetailPage /> : <Navigate to="/" replace />}
          />
          <Route
            path="/admin/planes"
            element={session.isPlatformAdmin ? <PlatformPlansPage /> : <Navigate to="/" replace />}
          />
          {/* Bienvenida tras el pago — sin gate de módulo: es la pantalla que
              confirma que la cuenta quedó viva, y tiene que poder verse
              cualquiera sea la combinación de módulos contratados. */}
          <Route path="/bienvenida" element={<WelcomePage />} />
          {/* El destino del botón de pagar de los correos de la demo. */}
          <Route path="/activar" element={<ActivarPage />} />
          <Route path="/email" element={session.modules.email ? <EmailPage /> : <Navigate to="/" replace />} />
          <Route path="/leads" element={session.modules.leads ? <LeadsPage /> : <Navigate to="/" replace />} />
          <Route path="/leads/:id" element={session.modules.leads ? <LeadDetailPage /> : <Navigate to="/" replace />} />
          <Route path="/embudos" element={session.modules.funnels ? <FunnelsListPage /> : <Navigate to="/" replace />} />
          <Route path="/embudos/:id" element={session.modules.funnels ? <FunnelsPage /> : <Navigate to="/" replace />} />
          <Route path="/embudos/:id/editar" element={session.modules.funnels ? <FunnelEditorPage /> : <Navigate to="/" replace />} />
          <Route path="/contacts" element={session.modules.contacts ? <ContactsPage /> : <Navigate to="/" replace />} />
          <Route path="/contacts/:id" element={session.modules.contacts ? <ContactDetailPage /> : <Navigate to="/" replace />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/reports" element={session.modules.reports ? <ReportsPage /> : <Navigate to="/" replace />} />
          <Route path="/reports/:id" element={session.modules.reports ? <ReportsPage /> : <Navigate to="/" replace />} />
          <Route path="/tasks" element={session.modules.tasks ? <TasksPage /> : <Navigate to="/" replace />} />
          {/* Bandeja antes que el triage: son dos pantallas del mismo canal,
              pero la bandeja es donde se conversa y el triage donde se decide
              de quién es un remitente. */}
          <Route path="/mensajes" element={session.modules.messaging ? <MessagesPage /> : <Navigate to="/" replace />} />
          <Route path="/mensajes/triage" element={session.modules.messaging ? <MessagingTriagePage /> : <Navigate to="/" replace />} />
          <Route path="/formularios" element={session.modules.forms ? <FormsPage /> : <Navigate to="/" replace />} />
          <Route path="/formularios/:id" element={session.modules.forms ? <FormDetailPage /> : <Navigate to="/" replace />} />
          <Route path="/facturacion" element={session.modules.billing ? <BillingPage /> : <Navigate to="/" replace />} />
          <Route path="/facturacion/:type/:id" element={session.modules.billing ? <BillingPage /> : <Navigate to="/" replace />} />
          <Route path="/showcase" element={<ShowcasePage />} />
          <Route path="/historial" element={<AssistantHistoryPage />} />
          <Route path="/memoria" element={<AssistantMemoryPage />} />
          <Route path="/proyectos" element={session.modules.projects ? <ProjectsPage /> : <Navigate to="/" replace />} />
          <Route path="/proyectos/:slug" element={session.modules.projects ? <ProjectDetailPage /> : <Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppShell>
    </SessionProvider>
  );
}
